import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, ArrowRight } from "lucide-react";

const Cart = () => {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const shipping = items.length === 0 ? 0 : 30;
  const total = subtotal + shipping;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <ShoppingBag className="h-14 w-14 mx-auto text-neutral-300" />
        <h1 className="mt-4 text-2xl font-extrabold text-[#0b2960]">Votre panier est vide</h1>
        <p className="text-neutral-600 mt-2">Découvrez nos offres et commencez vos achats à prix déstocké.</p>
        <Link to="/boutique">
          <Button className="mt-6 bg-[#e11d24] hover:bg-[#b8171d] h-11 px-6 font-bold">Voir la boutique</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl md:text-3xl font-extrabold text-[#0b2960]">Mon panier</h1>
      <p className="text-sm text-neutral-500 mt-1">{items.length} article{items.length > 1 ? "s" : ""} dans votre panier</p>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6 mt-6">
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <ul className="divide-y divide-neutral-200">
            {items.map((it) => (
              <li key={it.id} className="p-4 flex gap-4 items-center">
                <Link to={`/produit/${it.id}`} className="h-20 w-20 shrink-0 rounded-md overflow-hidden bg-neutral-100 flex items-center justify-center">
                  {it.image ? (
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[9px] uppercase tracking-widest font-semibold text-neutral-400">Photo</span>
                  )}
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">{it.brand}</div>
                  <Link to={`/produit/${it.id}`} className="block text-sm font-semibold text-neutral-800 hover:text-[#0b2960] line-clamp-2">{it.name}</Link>
                  <div className="text-sm font-bold text-[#e11d24] mt-1">{it.price.toFixed(2)} €</div>
                </div>
                <div className="inline-flex items-center border border-neutral-300 rounded-md">
                  <button onClick={() => updateQty(it.id, it.qty - 1)} className="h-9 w-9 flex items-center justify-center hover:bg-neutral-100"><Minus className="h-3.5 w-3.5" /></button>
                  <span className="w-8 text-center text-sm font-semibold">{it.qty}</span>
                  <button onClick={() => updateQty(it.id, it.qty + 1)} className="h-9 w-9 flex items-center justify-center hover:bg-neutral-100"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <button onClick={() => removeItem(it.id)} className="text-neutral-400 hover:text-[#e11d24]" aria-label="Supprimer">
                  <Trash2 className="h-5 w-5" />
                </button>
              </li>
            ))}
          </ul>
          <div className="p-4 flex items-center justify-between border-t border-neutral-200 bg-neutral-50">
            <Link to="/boutique" className="text-sm font-semibold text-[#0b2960] hover:text-[#e11d24] flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" /> Continuer mes achats
            </Link>
            <button onClick={clear} className="text-sm text-neutral-500 hover:text-[#e11d24]">Vider le panier</button>
          </div>
        </div>

        <aside className="bg-white border border-neutral-200 rounded-lg p-6 h-fit sticky top-40">
          <h3 className="font-bold text-[#0b2960] text-lg mb-4">Récapitulatif</h3>
          <div className="space-y-2 text-sm text-neutral-700">
            <div className="flex justify-between">
              <span>Sous-total</span>
              <span className="font-semibold">{subtotal.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between">
              <span>Livraison</span>
              <span className="font-semibold">{shipping === 0 ? "—" : `${shipping.toFixed(2)} €`}</span>
            </div>
            {subtotal > 0 && (
              <div className="text-xs bg-blue-50 text-[#0b2960] border border-blue-100 rounded-md p-2">
                Livraison à domicile : <b>30 €</b> sur Marseille et alentours. Retrait en magasin gratuit.
              </div>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-neutral-200 flex justify-between items-baseline">
            <span className="font-bold text-neutral-800">Total TTC</span>
            <span className="text-2xl font-black text-[#e11d24]">{total.toFixed(2)} €</span>
          </div>
          <Button onClick={() => navigate("/commander")} className="mt-5 w-full h-12 bg-[#e11d24] hover:bg-[#b8171d] text-white font-bold text-base">
            Commander <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div className="mt-4 text-xs text-neutral-500 text-center">Paiement en 1 fois · Sur place au magasin ou à la livraison</div>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
